import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  IconButton,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { makeStyles } from "@mui/styles";
import DatePicker from "react-datepicker";
import CustomButton from "../../Custom/CustomButton";
import instance from "../../../globals/axios";
import { getFarmerPlotId } from "../../../globals/utils";
import { useAlert } from "../../Alert";
import EditIcon from "../../../assets/svg/EditIcon.svg";
import SuccessPopup from "./SuccessPopup";

const useStyles = makeStyles((theme) => ({
  closeButton: {
    position: "absolute",
    top: theme.spacing(1),
    right: theme.spacing(1),
    fontSize: 16,
  },
  datepicker: {
    display: "none",
  },
}));

const GerminationPopup = ({ onClose, t }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [germinationPercentage, setGerminationPercentage] = useState("");
  const [comment, setComment] = useState("");
  const { showAlert } = useAlert();
  const [existingData, setExistingData] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [errors, setErrors] = useState({
    selectedDate: "",
    germinationPercentage: "",
  });
  let farmerPlotId = getFarmerPlotId();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await instance(
          `plotengagement/get-germination?farmer_plot=${farmerPlotId}`
        );
        let data = response.data;
        if (data.length > 0) {
          setExistingData(data[0]);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    if (farmerPlotId) {
      fetchData();
    }
  }, [farmerPlotId]);

  useEffect(() => {
    if (existingData) {
      const { date_of_visit, germination_percentage, comments } = existingData;
      setSelectedDate(date_of_visit ? new Date(date_of_visit) : null);
      setGerminationPercentage(germination_percentage || "");
      setComment(comments || "");
    }
  }, [existingData]);

  const handleDateChange = (date) => setSelectedDate(date);
  const handleGerminationChange = (event) =>
    setGerminationPercentage(event.target.value);
  const handleCommentChange = (event) => setComment(event.target.value);

  const validateForm = () => {
    let formValid = true;
    let updatedErrors = {};

    if (!selectedDate) {
      updatedErrors.selectedDate = t("Please select a date.");
      formValid = false;
    }
    if (!germinationPercentage) {
      updatedErrors.germinationPercentage = t(
        "Please enter germination percentage."
      );
      formValid = false;
    }

    setErrors((prevErrors) => ({
      ...prevErrors,
      ...updatedErrors,
    }));

    return formValid;
  };

  const handleClose = () => {
    setOpen(false);
    onClose();
  };

  const handleSubmit = () => {
    const isFormValid = validateForm();
    if (isFormValid) {
      const formatDate = (date) => {
        const pad = (num) => (num < 10 ? `0${num}` : `${num}`);
        return date
          ? `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
              date.getDate()
            )}`
          : null;
      };
      const formData = {
        date_of_visit: selectedDate ? formatDate(selectedDate) : null,
        germination_percentage: parseInt(germinationPercentage),
        comments: comment, // Include comment in formData
      };

      if (isEditMode) {
        formData.id = existingData.id;
      } else {
        formData.farmer_plot = farmerPlotId;
      }

      const apiUrl = isEditMode
        ? "plotengagement/update-germination"
        : "plotengagement/create-germination";
      instance
        .post(apiUrl, formData)
        .then(() => {
          setShowSuccessPopup(true);
          setTimeout(() => {
            setShowSuccessPopup(false);
            onClose();
          }, 2000);
        })
        .catch((err) => {
          console.error("error updating/creating germination", err);
          showAlert("error", "Some Error Occurred");
        });
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth>
      <div style={{ padding: "16px" }}>
        <Grid container>
          <Grid item xs={10}>
            <DialogTitle>{t("Germination")}</DialogTitle>
          </Grid>
          {existingData && (
            <Grid item xs={1} style={{ marginTop: "10px" }}>
              <img src={EditIcon} onClick={() => setIsEditMode(true)} />
            </Grid>
          )}
          <Grid item xs={1}>
            <IconButton
              aria-label="close"
              className={classes.closeButton}
              onClick={handleClose}
            >
              <CloseIcon />
            </IconButton>
          </Grid>
        </Grid>

        <Grid container style={{ marginTop: "20px" }}>
          <Grid item xs={7} style={{ marginBottom: "20px" }}>
            <Typography>{t("Date of Visit")}</Typography>
          </Grid>
          <Grid item xs={5} style={{ marginBottom: "20px" }}>
            <TextField
              value={selectedDate ? selectedDate.toLocaleDateString() : ""}
              onClick={() => document.getElementById("date-picker").click()}
              InputProps={{ readOnly: true }}
              helperText={errors.selectedDate}
              error={!!errors.selectedDate}
            />
            <DatePicker
              id="date-picker"
              selected={selectedDate}
              onChange={handleDateChange}
              className={classes.datepicker}
            />
          </Grid>
          <Grid item xs={6}>
            <Typography>{t("Germination %")}</Typography>
          </Grid>
          <Grid item xs={6}>
            <TextField
              value={germinationPercentage}
              onChange={handleGerminationChange}
              helperText={errors.germinationPercentage}
              error={!!errors.germinationPercentage}
            />
          </Grid>
          <Grid item xs={12} style={{ display: "grid", marginTop: "20px" }}>
            <Typography>{t("Comments")}</Typography>
            <TextField
              multiline
              rows={1}
              value={comment}
              onChange={handleCommentChange}
              disabled={!isEditMode && existingData !== null}
            />
          </Grid>
        </Grid>

        <div style={{ marginTop: "20px", textAlign: "center" }}>
          <CustomButton onClick={handleSubmit} hideIcon>
            {isEditMode ? t("Update") : t("Submit")}
          </CustomButton>
        </div>
      </div>
      {showSuccessPopup && (
        <SuccessPopup onClose={() => setShowSuccessPopup(false)} />
      )}
    </Dialog>
  );
};

export default GerminationPopup;
